/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import Dropdown from '../components/menus/Dropdown';

export interface UnitChooserProps {
  options: UnitOption[];
  fieldLabel?: string;
  defaultValue?: UnitOption['value'];
  readOnly?: boolean;
  onChange?: (val: string) => void;
  required?: boolean;
  error?: string;
  hasError?: boolean;
};

export interface UnitOption {
  label: string;
  value: string;
  disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * @UnitChooser
 *
 * @param {Array} options: options to be displayed in the dropdown
 * @param {string} fieldLabel: field label displayed above input
 * @param {boolean} hasError: If the field has an error the value is passed on
 *   to the dropdown component
 * @param {string | object} defaultValue: Default value passed to the component.
 *   Will only be considered if passed as UnitOption.
 * @param {boolean} readOnly: If true then dropdown is disabled
 * @param {Function} onChange: Callback fired with the selected value when a
 *   selection is made
 *
 * ```typescript
 * import {UnitChooser} from '@ion/dls/src/editors/';
 *
 * <UnitChooser options={options}
 *   fieldLabel={translate('common:fieldLabel')}
 *   defaultValue='mi'
 *   hasError={true}
 *   readOnly={false}
 *   onChange={handleChange} />
 *
 * ```
 */
export const UnitChooser = ({
  options,
  fieldLabel,
  hasError,
  defaultValue,
  readOnly,
  onChange
}: UnitChooserProps) => {
  const classes = useStyles();
  const [selected, setSelected] = useState(defaultValue || 'Default');

  useEffect(() => {
    const matchIndex = options.findIndex(option => option.value === defaultValue);
    const matchingOption = options[matchIndex];
    if (defaultValue) {
      setSelected(matchingOption?.label);
    }
  }, []);

  function handleChange({target: {value}}: ChangeEvent<HTMLSelectElement>) {
    const matchIndex = options.findIndex(option => option.value === value);
    const matchingOption = options[matchIndex];
    setSelected(matchingOption.label);
    onChange?.(matchingOption.value);
  }

  return (
    <div className={classes.root}>
      <Dropdown options={options}
        fieldLabel={fieldLabel || null}
        hasError={hasError}
        disabled={readOnly}
        value={selected}
        labelAsValue={false}
        onChange={handleChange} />
    </div>
  );
};

export default UnitChooser;
